import React, { useEffect, useRef, useState } from "react";
import {
    BarChart3,
    Workflow,
    Users,
    Receipt,
    FileSignature,
    Briefcase,
    ShoppingCart,
    ChevronLeft,
    ChevronRight,
} from "lucide-react";

/* ============================================================
   Per-slide visual components — each is a polished, distinct
   mockup of one Scitus product.
   ============================================================ */

const MativexSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-mativex">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Mativex · Today</div>
                <div className="slide-title">Real-time sales pulse</div>
            </div>
            <span className="slide-pill">
                <span className="live-dot" /> Live
            </span>
        </div>

        <div className="kpi-row">
            {[
                { l: "Revenue", v: "₹ 12.4L", c: "+18%" },
                { l: "Orders", v: "284", c: "+9%" },
                { l: "AOV", v: "₹ 4,367", c: "+3%" },
            ].map((k) => (
                <div key={k.l} className="kpi-tile">
                    <div className="kpi-l">{k.l}</div>
                    <div className="kpi-v">{k.v}</div>
                    <div className="kpi-c">{k.c}</div>
                </div>
            ))}
        </div>

        <div className="chart-card">
            <div className="chart-head">
                <span>Hourly sales · last 7 days</span>
                <span className="legend">
                    <span className="dot-teal" /> This week
                    <span className="dot-dim" /> Last week
                </span>
            </div>
            <svg
                viewBox="0 0 320 120"
                width="100%"
                height="120"
                preserveAspectRatio="none"
                style={{ marginTop: 6 }}
            >
                <defs>
                    <linearGradient id="pulseFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#00b4d8" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="#00b4d8" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {/* grid */}
                {[20, 50, 80].map((y) => (
                    <line
                        key={y}
                        x1="0"
                        x2="320"
                        y1={y}
                        y2={y}
                        stroke="rgba(255,255,255,0.05)"
                    />
                ))}
                {/* last week (dim) */}
                <polyline
                    fill="none"
                    stroke="rgba(139,148,158,0.5)"
                    strokeWidth="1.5"
                    strokeDasharray="4 4"
                    points="0,72 30,68 60,60 90,72 120,52 150,58 180,46 210,55 240,40 270,48 300,38 320,42"
                />
                {/* this week filled */}
                <path
                    d="M0,80 L30,72 L60,55 L90,62 L120,40 L150,48 L180,30 L210,38 L240,22 L270,28 L300,18 L320,22 L320,120 L0,120 Z"
                    fill="url(#pulseFill)"
                />
                <polyline
                    fill="none"
                    stroke="#00b4d8"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points="0,80 30,72 60,55 90,62 120,40 150,48 180,30 210,38 240,22 270,28 300,18 320,22"
                />
            </svg>
        </div>
    </div>
);

const VexenSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-vexen">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Vexen · Projects</div>
                <div className="slide-title">Operations command center</div>
            </div>
            <span className="slide-pill teal">4 active</span>
        </div>

        <div className="project-list">
            {[
                { p: "Tower B — Phase 1", pct: 82, st: "On track" },
                { p: "Hyderabad Plant Expansion", pct: 56, st: "Watch" },
                { p: "Eastside Warehouse Fit-out", pct: 94, st: "On track" },
                { p: "Downtown Showroom Rollout", pct: 38, st: "Delayed" },
            ].map((row) => (
                <div key={row.p} className="project-row">
                    <div className="project-meta">
                        <span className="project-name">{row.p}</span>
                        <span
                            className={`status ${
                                row.st === "Delayed"
                                    ? "warn"
                                    : row.st === "Watch"
                                      ? "amber"
                                      : "ok"
                            }`}
                        >
                            {row.st}
                        </span>
                    </div>
                    <div className="bar-track">
                        <div
                            className="bar-fill"
                            style={{ width: `${row.pct}%` }}
                        />
                    </div>
                    <div className="bar-foot">
                        <span>{row.pct}% complete</span>
                        <span>Schedule · on plan</span>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const KaryaSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-karya">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Karya · Today</div>
                <div className="slide-title">Attendance & roster</div>
            </div>
            <span className="slide-pill teal">199 staff</span>
        </div>

        <div className="kpi-row">
            {[
                { l: "Present", v: "184", c: "92%" },
                { l: "On leave", v: "12", c: "Planned" },
                { l: "Absent", v: "3", c: "Flagged" },
            ].map((k) => (
                <div key={k.l} className="kpi-tile">
                    <div className="kpi-l">{k.l}</div>
                    <div className="kpi-v">{k.v}</div>
                    <div className="kpi-c">{k.c}</div>
                </div>
            ))}
        </div>

        <div className="chart-card" style={{ paddingTop: 12 }}>
            <div className="chart-head">
                <span>Recent check-ins</span>
                <span className="legend muted-text">Live</span>
            </div>
            {[
                { n: "Aarav Sharma", r: "Store Manager · North", ts: "09:02" },
                { n: "Maria Garcia", r: "Cashier · East", ts: "08:48" },
                { n: "Liam O'Connor", r: "Floor Lead · West", ts: "09:14" },
            ].map((p, i) => (
                <div key={p.n} className="checkin-row">
                    <div className="avatar">{p.n[0]}</div>
                    <div className="checkin-meta">
                        <div className="ci-name">{p.n}</div>
                        <div className="ci-role">{p.r}</div>
                    </div>
                    <div className="ci-ts">{p.ts}</div>
                </div>
            ))}
        </div>
    </div>
);

const VyaaharSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-vyaahar">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Vyaahar · Receivables</div>
                <div className="slide-title">Cashflow this month</div>
            </div>
            <span className="slide-pill teal">Tax-ready</span>
        </div>

        <div className="cashflow">
            <div className="cashflow-amt">₹ 8.42L</div>
            <div className="cashflow-sub">Collected · 64% of target</div>
            <div className="bar-track lg">
                <div className="bar-fill" style={{ width: "64%" }} />
            </div>
        </div>

        <div className="chart-card" style={{ paddingTop: 12 }}>
            <div className="chart-head">
                <span>Pending invoices</span>
                <span className="legend">4 overdue</span>
            </div>
            {[
                { c: "Acme Co", a: "₹ 84,200", st: "Paid" },
                { c: "Northwind Co", a: "₹ 1,12,400", st: "Due" },
                { c: "Globex Ltd", a: "₹ 47,300", st: "Paid" },
                { c: "Initech", a: "₹ 2,06,800", st: "Overdue" },
            ].map((r) => (
                <div key={r.c} className="invoice-row">
                    <span className="inv-c">{r.c}</span>
                    <span className="inv-a">{r.a}</span>
                    <span
                        className={`inv-st ${
                            r.st === "Overdue"
                                ? "warn"
                                : r.st === "Due"
                                  ? "amber"
                                  : "ok"
                        }`}
                    >
                        {r.st}
                    </span>
                </div>
            ))}
        </div>
    </div>
);

const AttestraSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-attestrasign">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Attestra · MSA-2026-014</div>
                <div className="slide-title">Master Service Agreement</div>
            </div>
            <span className="slide-pill teal">Audit-trail</span>
        </div>

        <div className="contract-card">
            <div className="contract-lines">
                <div className="line w-full" />
                <div className="line w-85" />
                <div className="line w-70" />
                <div className="line w-95" />
                <div className="line w-60" />
            </div>

            <div className="signatures">
                <div className="sig-title">Awaiting 1 of 3 signatures</div>
                {[
                    { n: "Aarav Sharma · CEO", st: "Signed", ts: "11 May" },
                    {
                        n: "Maria Garcia · Counterparty",
                        st: "Signed",
                        ts: "13 May",
                    },
                    {
                        n: "Witness — Karya HR",
                        st: "Awaiting",
                        ts: "—",
                    },
                ].map((p) => (
                    <div key={p.n} className="sig-row">
                        <div className="avatar small">{p.n[0]}</div>
                        <div className="sig-meta">
                            <div className="ci-name">{p.n}</div>
                            <div className="ci-role">{p.ts}</div>
                        </div>
                        <span
                            className={`sig-status ${
                                p.st === "Signed" ? "ok" : "pending"
                            }`}
                        >
                            {p.st}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    </div>
);

const AcriumSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-acrium">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Acrium · Tower B · Phase 1</div>
                <div className="slide-title">Committed vs. budget — live</div>
            </div>
            <span className="slide-pill teal">Boardroom view</span>
        </div>

        <div className="kpi-row">
            {[
                { l: "Budget", v: "₹ 42.0Cr", c: "Approved" },
                { l: "Committed", v: "₹ 31.7Cr", c: "75.5%" },
                { l: "Variance", v: "+ ₹ 18L", c: "+0.4%" },
            ].map((k) => (
                <div key={k.l} className="kpi-tile">
                    <div className="kpi-l">{k.l}</div>
                    <div className="kpi-v">{k.v}</div>
                    <div className="kpi-c">{k.c}</div>
                </div>
            ))}
        </div>

        <div className="project-list" style={{ marginTop: 10 }}>
            {[
                { p: "Structural · RMC + Steel", pct: 88, st: "On budget" },
                { p: "MEP · Procurement Pkg-3", pct: 62, st: "Watch" },
                { p: "Finishes · Tile + Stone", pct: 41, st: "On budget" },
                { p: "Façade · Glazing Pkg-1", pct: 27, st: "Delayed" },
            ].map((row) => (
                <div key={row.p} className="project-row">
                    <div className="project-meta">
                        <span className="project-name">{row.p}</span>
                        <span
                            className={`status ${
                                row.st === "Delayed"
                                    ? "warn"
                                    : row.st === "Watch"
                                      ? "amber"
                                      : "ok"
                            }`}
                        >
                            {row.st}
                        </span>
                    </div>
                    <div className="bar-track">
                        <div
                            className="bar-fill"
                            style={{ width: `${row.pct}%` }}
                        />
                    </div>
                    <div className="bar-foot">
                        <span>{row.pct}% committed</span>
                        <span>vs. budget</span>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const KretaraSlide = () => (
    <div className="slide-inner" data-testid="hero-slide-kretara">
        <div className="slide-head">
            <div>
                <div className="slide-eyebrow">Kretara · Category · Apparel</div>
                <div className="slide-title">Procurement intelligence</div>
            </div>
            <span className="slide-pill teal">Live spend</span>
        </div>

        <div className="kpi-row">
            {[
                { l: "Spend MTD", v: "₹ 4.82 Cr", c: "−6.1% vs LM" },
                { l: "On-time %", v: "92.4%", c: "+1.8 pts" },
                { l: "Price vs bench", v: "−2.3%", c: "184 SKUs" },
            ].map((k) => (
                <div key={k.l} className="kpi-tile">
                    <div className="kpi-l">{k.l}</div>
                    <div className="kpi-v">{k.v}</div>
                    <div className="kpi-c">{k.c}</div>
                </div>
            ))}
        </div>

        <div className="project-list" style={{ marginTop: 10 }}>
            {[
                { p: "Vendor A · Knitwear", pct: 94, st: "Top tier" },
                { p: "Vendor B · Denim", pct: 76, st: "On budget" },
                { p: "Vendor C · Outerwear", pct: 58, st: "Watch" },
                { p: "Vendor D · Accessories", pct: 33, st: "Delayed" },
            ].map((row) => (
                <div key={row.p} className="project-row">
                    <div className="project-meta">
                        <span className="project-name">{row.p}</span>
                        <span
                            className={`status ${
                                row.st === "Delayed"
                                    ? "warn"
                                    : row.st === "Watch"
                                      ? "amber"
                                      : "ok"
                            }`}
                        >
                            {row.st}
                        </span>
                    </div>
                    <div className="bar-track">
                        <div
                            className="bar-fill"
                            style={{ width: `${row.pct}%` }}
                        />
                    </div>
                    <div className="bar-foot">
                        <span>Vendor score {row.pct}</span>
                        <span>last 90 days</span>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const SLIDES = [
    { key: "mativex", name: "Mativex", icon: BarChart3, body: <MativexSlide /> },
    { key: "vexen", name: "Vexen", icon: Workflow, body: <VexenSlide /> },
    { key: "karya", name: "Karya", icon: Users, body: <KaryaSlide /> },
    { key: "vyaahar", name: "Vyaahar", icon: Receipt, body: <VyaaharSlide /> },
    {
        key: "attestra",
        name: "Attestra",
        icon: FileSignature,
        body: <AttestraSlide />,
    },
    { key: "acrium", name: "Acrium", icon: Briefcase, body: <AcriumSlide /> },
    { key: "kretara", name: "Kretara", icon: ShoppingCart, body: <KretaraSlide /> },
];

const AUTOPLAY_MS = 5200;

export const HeroCarousel = () => {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (paused) return undefined;
        timer.current = setTimeout(
            () => setIndex((i) => (i + 1) % SLIDES.length),
            AUTOPLAY_MS
        );
        return () => clearTimeout(timer.current);
    }, [index, paused]);

    const go = (i) => setIndex((i + SLIDES.length) % SLIDES.length);

    return (
        <div
            className="hero-carousel"
            data-testid="hero-carousel"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
        >
            {/* Decorative back-cards for depth */}
            <div className="hero-back-card back-2" aria-hidden />
            <div className="hero-back-card back-1" aria-hidden />

            <div className="hero-carousel-frame">
                <div className="frame-chrome">
                    <span className="dot r" />
                    <span className="dot y" />
                    <span className="dot g" />
                    <span className="frame-url">
                        app.scitus.ai / {SLIDES[index].name.toLowerCase()}
                    </span>
                </div>

                <div className="frame-stage">
                    {SLIDES.map((s, i) => (
                        <div
                            key={s.key}
                            className={`slide ${i === index ? "active" : ""}`}
                            aria-hidden={i !== index}
                        >
                            {s.body}
                        </div>
                    ))}
                </div>
            </div>

            <div className="carousel-controls">
                <button
                    type="button"
                    className="ctrl-arrow"
                    onClick={() => go(index - 1)}
                    aria-label="Previous slide"
                    data-testid="hero-carousel-prev"
                >
                    <ChevronLeft size={16} />
                </button>

                <div className="dots" role="tablist" aria-label="Product slides">
                    {SLIDES.map((s, i) => (
                        <button
                            key={s.key}
                            type="button"
                            role="tab"
                            aria-selected={i === index}
                            aria-label={`Show ${s.name}`}
                            className={`dot-btn ${i === index ? "on" : ""}`}
                            onClick={() => go(i)}
                            data-testid={`hero-carousel-dot-${s.key}`}
                        >
                            <span className="d-label">
                                <s.icon size={12} />
                                {s.name}
                            </span>
                            <span className="d-progress">
                                <span
                                    className="d-progress-fill"
                                    style={{
                                        animationDuration: `${AUTOPLAY_MS}ms`,
                                        animationPlayState:
                                            paused || i !== index
                                                ? "paused"
                                                : "running",
                                        opacity: i === index ? 1 : 0,
                                    }}
                                />
                            </span>
                        </button>
                    ))}
                </div>

                <button
                    type="button"
                    className="ctrl-arrow"
                    onClick={() => go(index + 1)}
                    aria-label="Next slide"
                    data-testid="hero-carousel-next"
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default HeroCarousel;
